// ABOUTME: Compound tabs component with content panels built on Radix UI
// ABOUTME: Use for tabs with embedded content, supports ref for programmatic control

import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cn } from "@shared/lib/utils"
import { CHorizontalScroll } from "./CHorizontalScroll"

// Base Tabs components
const TabsRoot = TabsPrimitive.Root

const TabsList = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn("inline-flex h-9 items-center justify-center rounded-lg bg-gray-100 p-1 text-gray-500", className)}
    {...props}
  />
))
TabsList.displayName = "TabsList"

const TabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-white transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow",
      className,
    )}
    {...props}
  />
))
TabsTrigger.displayName = "TabsTrigger"

const TabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
))
TabsContent.displayName = "TabsContent"

// CTabsCompound component with Flowbite-compatible API
export interface CTabsCompoundProps {
  onActiveTabChange?: (activeTab: number) => void
  children?: React.ReactNode
  className?: string
  style?: "default" | "underline" | "pills" | "fullWidth" | "strip" | "chips"
  variant?: "default" | "underline" | "pills" | "fullWidth" | "strip" | "chips"
  /** Classes for the tab row itself. The legacy strip took its padding this way. */
  listClassName?: string
  /** Fade colour of the strip's scroll hints — match the background behind it. */
  fadeClassName?: string
}

export interface CTabsCompoundRef {
  /**
   * notify=false switches the strip WITHOUT the onActiveTabChange callback —
   * only for callers that already updated their own active-tab state and just
   * need the strip to follow (a guarded callback would re-ask or fight them).
   */
  setActiveTab: (tab: number, notify?: boolean) => void
}

export interface CTabItemCompoundProps {
  title: React.ReactNode
  icon?: React.ComponentType<{ className?: string }>
  disabled?: boolean
  active?: boolean
  children?: React.ReactNode
  className?: string
}

type CTabsCompoundComponent = React.ForwardRefExoticComponent<
  CTabsCompoundProps & React.RefAttributes<CTabsCompoundRef>
> & {
  Item: React.FC<CTabItemCompoundProps>
}

const styleClasses = {
  default: "",
  underline: "bg-transparent border-b rounded-none",
  // The page strip this screen had before it moved off the legacy CTabs: a full-width
  // row with a hairline under it, tabs sharing the width on a phone and sitting at the
  // start on a desktop. Heights come from the triggers, so the list must not fix one.
  strip: "flex w-full h-auto items-stretch justify-around gap-1 rounded-none bg-transparent p-0 md:justify-start",
  // The legacy CTabs type="pills": square-cornered chips that wrap on a phone, each
  // resting on its own grey plate. Distinct from `pills` above, which is round and
  // leaves inactive chips bare — that one belongs to the ration screen.
  chips:
    "flex w-full h-auto flex-wrap items-center justify-around gap-1 rounded-none bg-transparent p-0 md:flex-nowrap md:justify-start",
  // h-auto: pill cards are two-line (label + sublabel) and taller than the
  // list's default h-9 — a fixed height would clip them.
  pills: "bg-transparent gap-2 h-auto p-0",
  fullWidth: "w-full grid",
}

const triggerStyleClasses = {
  default: "",
  // Colours from the design (docs/design/Настройки.dc.html): a resting tab is dark enough
  // to read as a control (#4b5563), the active one is blue with a 2px underline. On a phone
  // the icon sits above the label and the tabs divide the width; on a desktop they read
  // along the row, so the first line of every label pins to the same height.
  chips:
    "h-auto whitespace-normal cursor-pointer rounded-md bg-gray-100 px-3 py-0 text-gray-600 max-md:flex-1 md:w-full data-[state=active]:bg-[#2F80ED] data-[state=active]:text-white data-[state=active]:shadow-none",
  strip:
    "h-auto whitespace-normal cursor-pointer rounded-none rounded-t-lg border-b-2 border-transparent text-gray-600 items-start justify-center max-md:flex-1 max-md:flex-col max-md:items-center max-md:justify-start max-md:gap-1 max-md:px-1 max-md:pt-2 max-md:pb-1 md:shrink-0 md:gap-2 md:px-3 md:pt-2.5 md:pb-1 data-[state=active]:border-[#2563eb] data-[state=active]:bg-transparent data-[state=active]:text-primary-600 data-[state=active]:shadow-none",
  underline:
    "rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none",
  pills: "rounded-full data-[state=active]:bg-primary data-[state=active]:text-white",
  fullWidth: "",
}

const CTabsCompoundBase = React.forwardRef<CTabsCompoundRef, CTabsCompoundProps>(
  ({ onActiveTabChange, children, className, listClassName, fadeClassName, style = "default", variant }, ref) => {
    const effectiveStyle = variant || style
    const [activeTab, setActiveTab] = React.useState("0")

    const handleValueChange = (value: string) => {
      setActiveTab(value)
      onActiveTabChange?.(parseInt(value, 10))
    }

    // The imperative switch takes the same path as a user click: without the
    // onActiveTabChange notification the caller's own active-tab state silently
    // diverges from the strip (content keyed off that state never switches).
    React.useImperativeHandle(ref, () => ({
      setActiveTab: (tab: number, notify = true) =>
        notify ? handleValueChange(String(tab)) : setActiveTab(String(tab)),
    }))

    const childrenArray = React.Children.toArray(children)
    const tabItems = childrenArray.filter((child): child is React.ReactElement<CTabItemCompoundProps> =>
      React.isValidElement(child),
    )

    // 18px is the icon size the strip is drawn with — spelled on the scale (4.5 * 4px), the way
    // the pre-compound TabOne spelled it, so `pnpm check:design` sees no pixel height. The gap
    // comes from the trigger rather than a margin, because on a phone the icon sits above the label.
    const iconClassName = effectiveStyle === "strip" ? "h-4.5 w-4.5 shrink-0" : "mr-2 h-4 w-4"

    const tabList = (
      <TabsList className={cn(styleClasses[effectiveStyle], listClassName)}>
        {tabItems.map((child, index) => (
          <TabsTrigger
            key={index}
            value={String(index)}
            disabled={child.props.disabled}
            className={cn(triggerStyleClasses[effectiveStyle], child.props.className)}
          >
            {child.props.icon && <child.props.icon className={iconClassName} />}
            {child.props.title}
          </TabsTrigger>
        ))}
      </TabsList>
    )

    // The strip scrolls sideways when the tabs outgrow the width, with a fade at the
    // edges — the legacy CTabs wrapped its row the same way, and without it a narrow
    // phone simply clipped the last tab.
    const tabRow =
      effectiveStyle === "strip" ? (
        <CHorizontalScroll
          className="border-b"
          fadeClassName={fadeClassName}
          showFadeOnDesktop
        >
          {tabList}
        </CHorizontalScroll>
      ) : effectiveStyle === "chips" ? (
        // Chips wrap instead of scrolling — the legacy strip put them in a plain box.
        <div className="border-b">{tabList}</div>
      ) : (
        tabList
      )

    return (
      <TabsRoot
        value={activeTab}
        onValueChange={handleValueChange}
        className={className}
      >
        {tabRow}
        {tabItems.map((child, index) => (
          <TabsContent
            key={index}
            value={String(index)}
          >
            {child.props.children}
          </TabsContent>
        ))}
      </TabsRoot>
    )
  },
)

CTabsCompoundBase.displayName = "CTabsCompound"

const CTabItemCompound: React.FC<CTabItemCompoundProps> = () => null

const CTabsCompoundWithStatics = Object.assign(CTabsCompoundBase, {
  Item: CTabItemCompound,
}) as CTabsCompoundComponent

// Exported under its own identifier: Fast Refresh (react-refresh/only-export-components)
// recognises a component by the name, and the Object.assign that attaches the statics is opaque to it.
export const CTabsCompound = CTabsCompoundWithStatics

// Export base components for potential reuse
export { TabsRoot, TabsList, TabsTrigger, TabsContent }
